import React from 'react';
import { BaseFormProblem, BaseFormSoftWarnings, DetailsFormProblem } from '../../SubmitGame/submission-errors';
import {
  BaseFormData,
  validateBaseForm,
  buildDefaultBaseFormData,
  validateFileLimitsHard,
  validateFileLimitsSoft,
} from '../GameBaseForm/baseFormHelper';
import { DetailsFormData, validateDetailsForm, buildDefaultDetailsFormData } from '../GameDetailsForm/detailsFormHelper';
import { RevenueShareFormData, buildDefaultRevenueShareFormData, validateRevenueShareForm } from '../RevenueShareForm/RevenueShareFormData';
import { DeveloperGame, Submission } from '../submission-graphql';
import { convertGameCoversArrayToObject } from '../../../../../../common/graphql/games/game-covers-helper';
import cleanCopy from '../../../../../../common/graphql/clean-copy';
import { buildEmptySubmissionVideo } from '../../../../../../common/graphql/games/game';
import { VideoUploadProgress } from '../../../Admin/Game/VideoUpload';
import { RevenueShareInputV3 } from '@crazygames/graph-types/revenue/revenue-share-v3.type';
import { isDefined } from '@crazygames/utils/isDefined';
import { getCurrentGameJam } from '@crazygames/gamejam/game-jam-config';
import { getLatestBuild } from '../../../../../../common/build-utils';
import { IframeLoaderConfig } from '@crazygames/game-types/src/game-build/loader-config/loaders/iframe';
import { SubmissionVideo } from '@crazygames/graph-types/submission/submission-video.type';
import { BaseFile } from '../../../../../../common/domain/upload';
import { GameBuild } from '../../../Admin/Game/gql/game-build-graphql';

export const INVITED_SUBMISSION_QUERY_PARAM = 'invitation_id';

interface SubmissionProviderData {
  detailsForm: DetailsFormData;
  baseForm: BaseFormData;
  detailsFormProblems?: DetailsFormProblem[];
  baseFormProblems?: BaseFormProblem[]; // problems of the files already uploaded
  uploadingFilesSoftProblems?: BaseFormSoftWarnings[]; // soft problems of all the files, including those currently being uploaded
  uploadingFilesHardProblems?: BaseFormProblem[]; // hard problems of all the files, including those currently being uploaded
  attemptedToSave?: boolean;
  submission?: Submission;
  game?: DeveloperGame;
  gameBuild?: GameBuild;
  revenueShareFormValid?: boolean;
  revenueShareForm: RevenueShareFormData;
  /** Reject or archived submissions cannot be edited at all. */
  isNonEditable: boolean;
  gameJamLocked: boolean;
  /**
   * Some game details (description, name, controls, tags, etc) cannot be edited anymore after the game is launched.
   * This will also be true for isNonEditable submissions.
   */
  disableDetailsEditing: boolean;
  modified: boolean;
  skipFileChecks: boolean;
}

export interface SubmissionContextData extends SubmissionProviderData {
  updateDetailsForm: (detailsForm: DetailsFormData) => void;
  updateBaseForm: (baseForm: BaseFormData) => void;
  onFilesToUploadSet: (baseForm: BaseFile[]) => void;
  updateRevenueShareForm: (revenueShareForm: RevenueShareFormData, isInvited: boolean) => void;
  setAttemptedToSave: (attemptedToSave: boolean) => void;
  setIsModified: (isModified: boolean, onSet?: () => void) => void;
  setBuildId: (buildId: string, callback?: () => void) => void;
  updateVideos: (videos: SubmissionVideo) => void;
  setVideoUploadProgress: (videoKey: string, progress: VideoUploadProgress) => void;
  updateSubmission: (submission: Submission) => void;
  /** Some screens need to reset context values, so that unsaved changes aren't submited by other screen. Read SubmissionProvider comment for more info. */
  resetContextState: () => void;
}

export const SubmissionContext = React.createContext<SubmissionContextData>({
  detailsForm: buildDefaultDetailsFormData(),
  baseForm: buildDefaultBaseFormData(),
  revenueShareForm: buildDefaultRevenueShareFormData(),
  detailsFormProblems: [],
  baseFormProblems: [],
  uploadingFilesSoftProblems: [],
  uploadingFilesHardProblems: [],
  attemptedToSave: false,
  revenueShareFormValid: false,
  isNonEditable: false,
  gameJamLocked: false,
  modified: false,
  disableDetailsEditing: false,
  skipFileChecks: false,
  updateDetailsForm: () => {},
  updateBaseForm: () => {},
  onFilesToUploadSet: () => {},
  updateRevenueShareForm: () => {},
  setAttemptedToSave: () => {},
  setIsModified: () => {},
  setBuildId: () => {},
  updateVideos: () => {},
  setVideoUploadProgress: () => {},
  updateSubmission: () => {},
  resetContextState: () => {},
});

interface SubmissionProviderProps {
  /** Initial submission is present when editing a submission. It's absence means this is a new submission */
  initialSubmission?: Submission;
  children?: React.ReactNode;
}

interface SubmissionState extends SubmissionContextData {}

/**
 * Submission context is left over since before the builds, basic lunch and new UI were introduced.
 * It has various issues, for example 2 separate screens, update game info and submit new version, need to send the entire context data when they are submitted.
 * This causes issues like having to reset/repopulate context data when closing those screens, so that unsaved changes from one screen don't leak into another screen's save.
 * A bigger refactor can be considered at some point, where each individual submission screen (base info, legal, details, update info, etc) manages its own state and doesn't need to share it with other screens.
 */
class SubmissionProvider extends React.Component<SubmissionProviderProps, SubmissionState> {
  constructor(props: SubmissionProviderProps) {
    super(props);
    this.state = this.computeStateFromSubmission();
  }

  private computeStateFromSubmission = () => {
    const { initialSubmission } = this.props;
    const initialGameBuild = getLatestBuild(initialSubmission?.gameBuilds || []);
    const initialGame = initialSubmission?.game;

    const detailsForm: DetailsFormData = initialSubmission
      ? {
          category: initialSubmission.categoryId,
          tags: initialSubmission.tagIds,
          description: initialSubmission.description,
          controls: initialSubmission.controls,
          playUrl: initialSubmission.playStoreLink,
          appstoreUrl: initialSubmission.appStoreLink,
          steamUrl: initialSubmission.steamStoreLink,
          playStoreDownloads: initialSubmission.playStoreDownloads ? `${initialSubmission.playStoreDownloads}` : null,
          appStoreDownloads: initialSubmission.appStoreDownloads ? `${initialSubmission.appStoreDownloads}` : null,
          steamDownloads: initialSubmission.steamDownloads ? `${initialSubmission.steamDownloads}` : null,
          gameCovers: convertGameCoversArrayToObject(initialSubmission.gameCovers),
          videos: {
            ...buildEmptySubmissionVideo(),
            ...initialSubmission.videos,
          },
          videoUploadProgress: {},
          allowEmbed: initialGameBuild?.allowEmbed || false,
        }
      : buildDefaultDetailsFormData();

    // TODO we are currently updating the entire submission flow, at some point we may want to get rid of details/base form in this context, and just store the plain fields
    // baseForm/detailsForm won't match anymore what's displayed on screen
    const baseForm: BaseFormData = initialSubmission
      ? ({
          name: initialSubmission.gameName,
          gameLoaderType: initialGameBuild?.loaderType,
          fullscreen: initialGameBuild?.fullscreen || 'NOT_SUPPORTED', // not supported as for the developers this means the "Supports fullscreen" checkbox is unchecked by default, as expected
          isIOSFriendly: !!initialGameBuild?.iosFriendly,
          isAndroidFriendly: !!initialGameBuild?.androidFriendly,
          orientation: initialGameBuild?.mobileOrientation || null,
          iframeLink: (JSON.parse(initialGameBuild!.loaderConfigJson) as IframeLoaderConfig).url || null,
          // similar to other places, we have 2 kind of files:
          // - the already installed files with a public URL from the build
          // - the uploaded files on client
          // given that we work here with uploaded client files, the build files need to be converted to them
          // can be improved at some point by having the same file interface for both
          files: (initialGameBuild?.files || []).map((file) => ({
            path: file.fileName,
            size: file.size,
            uploadId: file.uploadFileId,
            temporaryUrl: file.publicUrl,
          })),
          uploadInProgress: false,
          // the frontend for now handles only a single file name, however the backend stores an array
          unitySaveFileName: initialGameBuild?.unityConfig?.saveFileNames?.[0],
          progressSaveType: initialGameBuild?.apsConfig.progressType,
          hasIAP: initialGameBuild?.hasIap || false,
          isChromebookFriendly: !!initialGameBuild?.chromebookFriendly,
          multiplayerOptions: initialSubmission.multiplayerOptions ? cleanCopy(initialSubmission.multiplayerOptions, []) : undefined,
          gameJamId: initialSubmission.gameJamId,
        } as BaseFormData)
      : buildDefaultBaseFormData();

    const revenueShareForm = buildDefaultRevenueShareFormData();
    if (initialSubmission?.rawRevenueShareOptions) {
      const parsed = JSON.parse(initialSubmission.rawRevenueShareOptions) as RevenueShareInputV3;
      revenueShareForm.isEligible = parsed.isEligible;
      revenueShareForm.makeExclusive = parsed.makeExclusive;
      revenueShareForm.hasImplementedSDK = parsed.hasImplementedSDK;
      revenueShareForm.suitableForChildren = true; // requirement for already submitted games
      revenueShareForm.hasCustomContract = parsed.hasCustomContract;
      revenueShareForm.doesNotViolate = !parsed.hasCustomContract; // if no custom contract, then the game selected that it agrees to ToS
    }
    const currentGameJam = getCurrentGameJam(process.env.REACT_APP_CONFIG !== 'prod');
    const gameJamLocked = !!initialSubmission?.gameJamId && (!currentGameJam || currentGameJam.id !== initialSubmission.gameJamId);

    const isNonEditable = initialSubmission?.status === 'REJECTED' || initialSubmission?.gameStatus === 'ARCHIVED';
    // developers can update details only while the game wasn't yet launched/basic launched, keep this check in sync with graph logic
    const disableDetailsEditing = isDefined(initialSubmission) && initialSubmission.gameStatus !== 'SUBMISSION';

    const skipFileChecks = initialSubmission?.skipFileChecks ?? false;

    return {
      detailsFormProblems: validateDetailsForm(initialSubmission ?? null, detailsForm),
      baseFormProblems: validateBaseForm(baseForm, skipFileChecks),
      revenueShareFormValid: false,
      submission: initialSubmission,
      game: initialGame,
      gameBuild: initialGameBuild,
      revenueShareForm,
      detailsForm: detailsForm,
      baseForm: baseForm,
      isNonEditable,
      gameJamLocked: gameJamLocked,
      disableDetailsEditing: disableDetailsEditing || isNonEditable,
      modified: false,
      skipFileChecks,
      updateDetailsForm: this.updateDetailsForm,
      updateBaseForm: this.updateBaseForm,
      onFilesToUploadSet: this.onFilesToUploadSet,
      updateRevenueShareForm: this.updateRevenueShareForm,
      setAttemptedToSave: this.setAttemptedToSave,
      setIsModified: this.setIsModified,
      setBuildId: this.setBuildId,
      updateVideos: this.updateVideos,
      setVideoUploadProgress: this.setVideoUploadProgress,
      updateSubmission: this.updateSubmission,
      resetContextState: () => {
        this.setState({
          ...this.computeStateFromSubmission(),
          attemptedToSave: false,
        });
      },
    };
  };

  render() {
    return <SubmissionContext.Provider value={this.state}>{this.props.children}</SubmissionContext.Provider>;
  }

  // when dev submits a new version or changes game details new builds may be created, so the submission query will be refetched and needs to be updated here
  componentDidUpdate(prevProps: SubmissionProviderProps) {
    if (this.props.initialSubmission && this.props.initialSubmission !== prevProps.initialSubmission) {
      this.setState({ submission: this.props.initialSubmission }, () => {
        this.setState(this.computeStateFromSubmission());
      });
    }
  }

  private updateSubmission = (submission: Submission) => {
    this.setState({ submission });
  };

  private setAttemptedToSave = (attemptedToSave: boolean) => {
    this.setState({ attemptedToSave });
  };
  private setIsModified = (isModified: boolean, onSet?: () => void) => {
    this.setState({ modified: isModified }, onSet);
  };

  private updateDetailsForm = (detailsForm: DetailsFormData) => {
    const detailsFormProblems = validateDetailsForm(this.state.submission ?? null, detailsForm);
    this.setState({ detailsForm: detailsForm, detailsFormProblems: detailsFormProblems, modified: true });
  };

  private updateBaseForm = (baseForm: BaseFormData) => {
    const baseFormProblems = validateBaseForm(baseForm, this.state.skipFileChecks);
    this.setState({ baseForm: baseForm, baseFormProblems: baseFormProblems, modified: true });
  };

  private onFilesToUploadSet = <T extends BaseFile>(files: T[]) => {
    if (this.state.skipFileChecks) {
      return;
    }
    const softLimitsProblems = validateFileLimitsSoft(files);
    const hardLimitsProblems = validateFileLimitsHard(files || []);

    this.setState({
      uploadingFilesSoftProblems: softLimitsProblems,
      uploadingFilesHardProblems: hardLimitsProblems,
    });
  };

  private updateRevenueShareForm = (revenueShareForm: RevenueShareFormData, isInvited: boolean) => {
    const revenueShareFormValid = validateRevenueShareForm(revenueShareForm, isInvited).length === 0;
    this.setState({ revenueShareForm, revenueShareFormValid });
  };

  private updateVideos = (videos: SubmissionVideo) => {
    this.setState((prevState) => ({
      detailsForm: {
        ...prevState.detailsForm,
        videos: videos,
      },
    }));
  };

  private setBuildId = (buildId: string, callback?: () => void) => {
    this.setState(
      (prevState) => ({
        submission: prevState.submission && {
          ...prevState.submission,
          gameBuildId: buildId,
        },
      }),
      callback,
    );
  };

  private setVideoUploadProgress = (videoKey: string, progress: VideoUploadProgress) => {
    this.setState((prevState) => {
      const newDetailsForm = {
        ...prevState.detailsForm,
        videoUploadProgress: { ...prevState.detailsForm.videoUploadProgress, [videoKey]: progress },
      };
      const detailsFormProblems = validateDetailsForm(this.state.submission ?? null, newDetailsForm);

      return {
        detailsForm: newDetailsForm,
        detailsFormProblems,
      };
    });
  };
}

export default SubmissionProvider;
